import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import {
  FileText, ArrowLeft, Save, Eye, Sparkles, Hash, Calendar, Image,
  Loader2, Check, X, AlertTriangle, Megaphone, Globe, Type, Film,
  BookOpen, Mic, BarChart3, ChevronDown, Wand2, Copy,
} from "lucide-react";

const CONTENT_TYPES = [
  { value: "post", label: "Social Post", icon: Globe, color: "text-blue-600 bg-blue-50" },
  { value: "article", label: "Blog Article", icon: BookOpen, color: "text-green-600 bg-green-50" },
  { value: "reel", label: "Reel / Short Video", icon: Film, color: "text-pink-600 bg-pink-50" },
  { value: "story", label: "Story", icon: Image, color: "text-purple-600 bg-purple-50" },
  { value: "infographic", label: "Infographic", icon: BarChart3, color: "text-amber-600 bg-amber-50" },
  { value: "podcast", label: "Podcast Script", icon: Mic, color: "text-red-600 bg-red-50" },
  { value: "newsletter", label: "Newsletter", icon: FileText, color: "text-cyan-600 bg-cyan-50" },
];

const PLATFORMS = [
  { value: "instagram", label: "Instagram", color: "bg-gradient-to-r from-purple-500 to-pink-500" },
  { value: "facebook", label: "Facebook", color: "bg-blue-600" },
  { value: "twitter", label: "X (Twitter)", color: "bg-black" },
  { value: "linkedin", label: "LinkedIn", color: "bg-blue-700" },
  { value: "tiktok", label: "TikTok", color: "bg-black" },
  { value: "youtube", label: "YouTube", color: "bg-red-600" },
];

// ── Toast ──
function Toast({ message, type, onClose }: { message: string; type: "success" | "error"; onClose: () => void }) {
  useEffect(() => { const t = setTimeout(onClose, 4000); return () => clearTimeout(t); }, [onClose]);
  return (
    <div className={`fixed bottom-6 right-6 z-50 flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg text-white text-sm font-medium ${type === "success" ? "bg-green-600" : "bg-red-600"}`}>
      {type === "success" ? <Check className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
      {message}
      <button onClick={onClose} className="ml-2 hover:opacity-70"><X className="w-3.5 h-3.5" /></button>
    </div>
  );
}

export default function CreateContent() {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{message: string; type: "success"|"error"} | null>(null);
  const [campaigns, setCampaigns] = useState<any[]>([]);
  const [generating, setGenerating] = useState(false);

  // Form state
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [type, setType] = useState("post");
  const [campaignId, setCampaignId] = useState("");
  const [platforms, setPlatforms] = useState<string[]>([]);
  const [hashtags, setHashtags] = useState("");
  const [scheduledAt, setScheduledAt] = useState("");
  const [aiPrompt, setAiPrompt] = useState("");
  const [showAI, setShowAI] = useState(false);
  const [preview, setPreview] = useState(false);

  useEffect(() => {
    api.get<any>("/campaigns?limit=100").then(d => setCampaigns(d.campaigns || [])).catch(() => {});
  }, []);

  const togglePlatform = (p: string) => {
    setPlatforms(prev => prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p]);
  };

  const handleAiGenerate = useCallback(async () => {
    if (!aiPrompt.trim()) return;
    setGenerating(true);
    try {
      const data = await api.post<any>("/ai/generate/post", { prompt: aiPrompt, platform: platforms[0] || "instagram", tone: "professional" });
      if (data.content) setBody(data.content);
      if (data.hashtags) setHashtags(data.hashtags.join(" "));
      if (data.title) setTitle(data.title);
      setToast({ message: "AI content generated!", type: "success" });
    } catch {
      setToast({ message: "AI generation failed — write manually", type: "error" });
    }
    setGenerating(false);
  }, [aiPrompt, platforms]);

  const handleSave = async (asDraft = true) => {
    if (!title.trim()) { setToast({ message: "Title is required", type: "error" }); return; }
    if (!body.trim()) { setToast({ message: "Content body is required", type: "error" }); return; }
    setSaving(true);
    try {
      const payload: any = {
        title: title.trim(),
        body: body.trim(),
        type,
        platforms: platforms.length ? platforms : undefined,
        hashtags: hashtags.trim() || undefined,
        campaignId: campaignId || undefined,
        scheduledAt: scheduledAt || undefined,
        aiGenerated: showAI,
        aiPrompt: aiPrompt || undefined,
      };
      await api.post("/content", payload);
      setToast({ message: asDraft ? "Content saved as draft!" : "Content created!", type: "success" });
      setTimeout(() => navigate("/content"), 1200);
    } catch (err: any) {
      setToast({ message: err.message || "Failed to save content", type: "error" });
    }
    setSaving(false);
  };

  const charCount = body.length;
  const selectedType = CONTENT_TYPES.find(t => t.value === type);

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate("/content")} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <ArrowLeft className="w-5 h-5 text-gray-500" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Create Content</h1>
            <p className="text-sm text-gray-500 mt-0.5">Craft and schedule your content</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => setPreview(!preview)}
            className="flex items-center gap-2 px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
            <Eye className="w-4 h-4" /> Preview
          </button>
          <button onClick={() => handleSave(true)} disabled={saving}
            className="flex items-center gap-2 px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
            <Save className="w-4 h-4" /> Save Draft
          </button>
          <button onClick={() => handleSave(false)} disabled={saving}
            className="btn-primary text-sm flex items-center gap-2">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            Create Content
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Editor */}
        <div className="lg:col-span-2 space-y-6">
          {/* Title */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
            <label className="block text-sm font-semibold text-gray-900">Title <span className="text-red-500">*</span></label>
            <input
              type="text" value={title} onChange={e => setTitle(e.target.value)}
              placeholder="Enter a compelling title..."
              className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none"
            />
          </div>

          {/* Body */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
            <div className="flex items-center justify-between">
              <label className="block text-sm font-semibold text-gray-900">Content Body <span className="text-red-500">*</span></label>
              <span className={`text-xs ${charCount > 2200 ? "text-red-500" : "text-gray-400"}`}>
                {charCount.toLocaleString()} characters
              </span>
            </div>
            <textarea
              value={body} onChange={e => setBody(e.target.value)}
              placeholder="Write your content here... Use rich formatting, emojis, and compelling copy."
              rows={12}
              className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none resize-none font-mono leading-relaxed"
            />

            {/* Hashtags */}
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                <Hash className="w-4 h-4" /> Hashtags
              </label>
              <input
                type="text" value={hashtags} onChange={e => setHashtags(e.target.value)}
                placeholder="#marketing #growth #brand"
                className="w-full px-4 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none"
              />
            </div>
          </div>

          {/* AI Generator */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <button onClick={() => setShowAI(!showAI)}
              className="w-full flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-gradient-to-r from-violet-500 to-purple-500 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <div className="text-left">
                  <p className="text-sm font-semibold text-gray-900">AI Content Generator</p>
                  <p className="text-xs text-gray-500">Let AI draft your content in seconds</p>
                </div>
              </div>
              <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showAI ? "rotate-180" : ""}`} />
            </button>
            {showAI && (
              <div className="px-6 pb-6 space-y-3">
                <textarea
                  value={aiPrompt} onChange={e => setAiPrompt(e.target.value)}
                  placeholder="Describe what you want to create... e.g., 'Write an Instagram post about our new cloud hosting feature launch'"
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none resize-none"
                />
                <button onClick={handleAiGenerate} disabled={generating || !aiPrompt.trim()}
                  className="btn-primary text-sm flex items-center gap-2 disabled:opacity-50">
                  {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
                  {generating ? "Generating..." : "Generate with AI"}
                </button>
              </div>
            )}
          </div>

          {/* Preview Panel */}
          {preview && body && (
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Eye className="w-4 h-4" /> Preview
              </h3>
              <div className="border border-gray-100 rounded-xl p-4 bg-gray-50">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-r from-violet-500 to-purple-500" />
                  <div>
                    <p className="text-sm font-semibold">MigraTeck LLC</p>
                    <p className="text-xs text-gray-400">Just now</p>
                  </div>
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">{title}</h4>
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{body}</p>
                {hashtags && <p className="text-sm text-blue-600 mt-2">{hashtags}</p>}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Content Type */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-3">
            <label className="block text-sm font-semibold text-gray-900">Content Type</label>
            <div className="grid grid-cols-1 gap-2">
              {CONTENT_TYPES.map(t => (
                <button key={t.value} onClick={() => setType(t.value)}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all ${
                    type === t.value ? "ring-2 ring-purple-500 bg-purple-50/50" : "hover:bg-gray-50 border border-gray-100"
                  }`}>
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${t.color}`}>
                    <t.icon className="w-4 h-4" />
                  </div>
                  <span className={type === t.value ? "font-semibold text-purple-700" : "text-gray-700"}>{t.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Target Platforms */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-3">
            <label className="block text-sm font-semibold text-gray-900">Target Platforms</label>
            <div className="flex flex-wrap gap-2">
              {PLATFORMS.map(p => (
                <button key={p.value} onClick={() => togglePlatform(p.value)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                    platforms.includes(p.value)
                      ? `${p.color} text-white shadow-sm`
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}>
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {/* Campaign */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-3">
            <label className="flex items-center gap-2 text-sm font-semibold text-gray-900">
              <Megaphone className="w-4 h-4 text-blue-600" /> Link to Campaign
            </label>
            <select value={campaignId} onChange={e => setCampaignId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-purple-500 outline-none">
              <option value="">No campaign</option>
              {campaigns.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>

          {/* Schedule */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-3">
            <label className="flex items-center gap-2 text-sm font-semibold text-gray-900">
              <Calendar className="w-4 h-4 text-purple-600" /> Schedule
            </label>
            <input
              type="datetime-local" value={scheduledAt} onChange={e => setScheduledAt(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-purple-500 outline-none"
            />
            {scheduledAt && (
              <p className="text-xs text-green-600 flex items-center gap-1">
                <Calendar className="w-3 h-3" /> Scheduled for {new Date(scheduledAt).toLocaleString()}
              </p>
            )}
          </div>
        </div>
      </div>

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}
